import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import useCategoryMutations from "../../../data/useCategoryMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import Modal from "../../../layouts/Modal";
import { FormatType, ListingDuration } from "../../../models/EbayOffer";
import { IPartCategory } from "../../../models/Part";
import SelectInput from "../../../components/shared/Inputs/SelectInput";
import { NumberInput } from "../../../components/shared/Inputs/NumberInput";
import { EbayCategoryDropdown } from "../../ebayListings/EbayCategoryDropdown";
import { EbayStoreCategoryDropdown } from "../../ebayListings/EbayStoreCategoryDropdown";
import EbayListingTitle from "../../ebayListings/EbayListingTitle";
import useEbay from "../../../data/useEbay";
import LoadingWheel from "../../../components/shared/LoadingWheel";
import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import ConfirmModal from "../../../components/shared/Confirm/ConfirmModal";
import usePartDefaultCategoriesMutations from "../../../data/usePartDefaultCategoriesMutations";
import useNotification from "../../notifications/useNotifications";

const formatOptions = [
  { id: FormatType.Auction, name: "Auction" },
  { id: FormatType.FixedPrice, name: "Buy It Now" },
];

const durationOptions = [
  { id: ListingDuration.Days1, name: "1 Day" },
  { id: ListingDuration.Days3, name: "3 Days" },
  { id: ListingDuration.Days5, name: "5 Days" },
  { id: ListingDuration.Days7, name: "7 Days" },
  { id: ListingDuration.Days10, name: "10 Days" },
  { id: ListingDuration.Days21, name: "21 Days" },
  { id: ListingDuration.Days30, name: "30 Days" },
];

type FieldKey =
  | "width"
  | "height"
  | "length"
  | "weight"
  | "format"
  | "duration"
  | "auctionReservePrice"
  | "auctionStartPrice"
  | "buyItNowPrice"
  | "enableBestOffer"
  | "autoAcceptPrice"
  | "autoAcceptThreshold"
  | "fulfillmentPolicyId"
  | "paymentPolicyId"
  | "returnPolicyId"
  | "ebayCategoryId"
  | "ebayStoreCategoryId"
  | "locationKey"
  | "titleStructure";

interface FieldOption {
  id: FieldKey;
  name: string;
}

const fieldOptions: FieldOption[] = [
  { id: "width", name: "Width (cm)" },
  { id: "height", name: "Height (cm)" },
  { id: "length", name: "Length (cm)" },
  { id: "weight", name: "Weight (kg)" },
  { id: "format", name: "Listing Format" },
  { id: "duration", name: "Duration" },
  { id: "auctionReservePrice", name: "Auction Reserve Price (£)" },
  { id: "auctionStartPrice", name: "Auction Starting Price (£)" },
  { id: "buyItNowPrice", name: "Buy It Now Price (£)" },
  { id: "enableBestOffer", name: "Enable Best Offer" },
  { id: "autoAcceptPrice", name: "Best Offer Auto Accept Price (£)" },
  { id: "autoAcceptThreshold", name: "Best Offer Auto Accept Threshold (%)" },
  { id: "fulfillmentPolicyId", name: "Shipping Policy" },
  { id: "paymentPolicyId", name: "Payment Policy" },
  { id: "returnPolicyId", name: "Returns Policy" },
  { id: "ebayCategoryId", name: "eBay Category" },
  { id: "ebayStoreCategoryId", name: "Store Category" },
  { id: "locationKey", name: "Location" },
  { id: "titleStructure", name: "Title Structure" },
];

interface FormValues {
  selectedField: FieldKey | "";
  listingDefaults: {
    width: number,
    height: number,
    length: number,
    weight: number,
    format: FormatType,
    duration: ListingDuration,
    auctionReservePrice: number,
    auctionStartPrice: number,
    buyItNowPrice: number,
    enableBestOffer: boolean,
    autoAcceptPrice: number,
    autoAcceptThreshold: number,
    fulfillmentPolicyId: string,
    paymentPolicyId: string,
    returnPolicyId: string,
    ebayCategoryId: string | undefined,
    ebayStoreCategoryId: string | undefined,
    locationKey: string,
    titleStructure: string,
  }
};

interface BulkUpdateSingleCategoryDefaultProps {
  open: boolean;
  setOpen: any;
  selectedCategories: IPartCategory[];
  startingField?: FieldKey | null;
  startingValue?: any;
}

export default function BulkUpdateSingleCategoryDefault({ open, setOpen, selectedCategories, startingField, startingValue }: BulkUpdateSingleCategoryDefaultProps) {
  const { bulkPatchDefaultCategories } = useCategoryMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Default Categories");
  const { bulkApplyCategoryDefaultsToUnlistedForCategories } = usePartDefaultCategoriesMutations();
  const { addNotification } = useNotification();
  const { policies, locations } = useEbay();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [applyDefaultsOpen, setApplyDefaultsOpen] = useState<boolean>(false);
  const [isApplyingDefaults, setIsApplyingDefaults] = useState<boolean>(false);

  const { register, getValues, setValue, watch, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      selectedField: "",
      listingDefaults: {
        width: 0,
        height: 0,
        length: 0,
        weight: 0,
        format: FormatType.FixedPrice,
        duration: ListingDuration.GTC,
        auctionReservePrice: 0,
        auctionStartPrice: 0,
        buyItNowPrice: 0,
        enableBestOffer: false,
        autoAcceptPrice: 0,
        autoAcceptThreshold: 0,
        fulfillmentPolicyId: "",
        paymentPolicyId: "",
        returnPolicyId: "",
        ebayCategoryId: "",
        ebayStoreCategoryId: "",
        locationKey: "",
        titleStructure: "{{year}} {{make}} {{shortModel}} {{categoryName}}",
      }
    }
  });

  const selectedField = watch("selectedField");

  useEffect(() => {
    if (!policies.isLoading && policies.data && !locations.isLoading && locations.data) {

      if (startingField) {
        setValue("selectedField", startingField);

        if (startingValue !== undefined) {
          setValue(`listingDefaults.${startingField}`, startingValue);
        }
      }

      setIsLoading(false);
    }
  }, [policies.isLoading, locations.isLoading, policies.data, locations.data]);

  const handleSetFormat = (_: string, value: any) => {
    setValue("listingDefaults.duration",
      (value === FormatType.FixedPrice
        ? ListingDuration.GTC as number
        : ListingDuration.Days30 as number)
    );
    setValue("listingDefaults.format", value);
  };

  const onSubmit = (data: FormValues) => {
    if (!data.selectedField) {
      return;
    }

    // Build listingDefaults object with only the selected field
    const listingDefaults: any = {};
    listingDefaults[data.selectedField] = data.listingDefaults[data.selectedField];

    const body = {
      categoryIds: selectedCategories.map((cat) => cat.id),
      listingDefaults: listingDefaults,
    };

    bulkPatchDefaultCategories.mutate(body, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        reset();
        setApplyDefaultsOpen(true);
      }
    });
  };

  const handleApplyDefaultsToUnlisted = async () => {
    const categoryIds = selectedCategories.map((category) => category.id);

    if (categoryIds.length === 0) {
      setApplyDefaultsOpen(false);
      return;
    }

    setIsApplyingDefaults(true);

    try {
      await bulkApplyCategoryDefaultsToUnlistedForCategories({ categoryIds });

      setApplyDefaultsOpen(false);
      addNotification({
        variant: "success",
        primaryText: "Applied category defaults to unlisted offers",
      });
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Failed to apply defaults to unlisted offers",
      });
    } finally {
      setIsApplyingDefaults(false);
    }
  };

  const renderFieldInput = () => {
    if (!selectedField) {
      return (
        <div className="mt-6 p-4 bg-gray-50 rounded border border-gray-200">
          <p className="text-gray-600">Please select a field to update</p>
        </div>
      );
    }

    const fieldName = `listingDefaults.${selectedField}`;

    switch (selectedField) {
      case "width":
      case "height":
      case "length":
      case "weight":
        return (
          <NumberInput
            label={fieldOptions.find(f => f.id === selectedField)?.name || ""}
            register={register}
            registerName={fieldName}
            step="1"
          />
        );

      case "format":
        return (
          <SelectInput
            label="Listing Format"
            register={register}
            registerName={fieldName}
            options={formatOptions}
            setValue={handleSetFormat}
            watch={watch}
          />
        );

      case "duration":
        return (
          <SelectInput
            label="Duration"
            register={register}
            registerName={fieldName}
            options={durationOptions}
            setValue={setValue}
            watch={watch}
          />
        );

      case "auctionReservePrice":
      case "auctionStartPrice":
      case "buyItNowPrice":
      case "autoAcceptPrice":
      case "autoAcceptThreshold":
        return (
          <NumberInput
            label={fieldOptions.find(f => f.id === selectedField)?.name || ""}
            register={register}
            registerName={fieldName}
          />
        );

      case "enableBestOffer":
        return (
          <div className="sm:col-span-1 mt-4">
            <input
              id="enableBestOffer"
              {...register(fieldName as any)}
              type="checkbox"
              className="mr-2 h-5 w-5"
            />
            <label htmlFor="enableBestOffer">Allow Best Offer?</label>
          </div>
        );

      case "fulfillmentPolicyId":
        return (
          <SelectInput
            label="Shipping Policy*"
            register={register}
            registerName={fieldName}
            options={policies.data?.data.fulfilmentPolicies ?? []}
            setValue={setValue}
            watch={watch}
          />
        );

      case "paymentPolicyId":
        return (
          <SelectInput
            label="Payment Policy*"
            register={register}
            registerName={fieldName}
            options={policies.data?.data.paymentPolicies ?? []}
            setValue={setValue}
            watch={watch}
          />
        );

      case "returnPolicyId":
        return (
          <SelectInput
            label="Returns Policy*"
            register={register}
            registerName={fieldName}
            options={policies.data?.data.returnPolicies ?? []}
            setValue={setValue}
            watch={watch}
          />
        );

      case "ebayCategoryId":
        return (
          <EbayCategoryDropdown
            categoryId=""
            register={register}
            registerName={fieldName}
            setValue={setValue}
            watch={watch}
          />
        );

      case "ebayStoreCategoryId":
        return (
          <EbayStoreCategoryDropdown
            categoryId=""
            register={register}
            registerName={fieldName}
            setValue={setValue}
            watch={watch}
          />
        );

      case "locationKey":
        return (
          <SelectInput
            label="Location*"
            register={register}
            registerName={fieldName}
            options={locations.data?.data.locations ?? []}
            setValue={setValue}
            watch={watch}
          />
        );

      case "titleStructure":
        return (
          <EbayListingTitle
            register={register}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
            registerName={fieldName}
            isTemplate={true}
            // Aspect names are per-category, so only offer them when the edit targets one.
            templateCategory={selectedCategories.length === 1 ? selectedCategories[0] : null}
          />
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Modal open={open} setOpen={setOpen} width="max-w-7xl">
        <LoadingWheel />
      </Modal>
    );
  }

  return (
    <>
      <ConfirmModal
        open={applyDefaultsOpen}
        setOpen={setApplyDefaultsOpen}
        isLoading={isApplyingDefaults}
        title={"Apply Defaults to Unlisted Offers?"}
        message={`Defaults have been saved.\nWould you like to update all unlisted offers in ${selectedCategories.length} selected categories with the new defaults?`}
        confirmButtonText={"Apply Defaults"}
        onConfirm={handleApplyDefaultsToUnlisted}
      />
      <Modal open={open} setOpen={setOpen} width="max-w-7xl">
        <form onSubmit={handleSubmit(onSubmit)}>
          <h1 className="text-2xl">Bulk Update Single eBay Default</h1>
          <div className="mt-4 space-y-4 text-xl">
            Categories Selected: <span className="font-semibold">{selectedCategories?.slice(0, 20)?.map((cat) => cat.name).join(", ")}
              {selectedCategories.length > 20 ? `... +${selectedCategories.length - 20} more` : ""}
            </span>
          </div>

          <hr className="my-4" />

          <div className="mt-6">
            <h2 className="text-lg mb-4 font-semibold">Select Field to Update</h2>
            <SelectInput
              label="Field"
              register={register}
              registerName="selectedField"
              options={fieldOptions}
              setValue={setValue}
              watch={watch}
            />
          </div>

          {selectedField && (
            <>
              <hr className="my-4" />
              <div className="mt-6">
                <h2 className="text-lg mb-4 font-semibold">Update Value</h2>
                <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">
                  {renderFieldInput()}
                </div>
              </div>
            </>
          )}

          <div className="mt-6">
            <ButtonInput
              label="Save"
              isSubmit={true}
              classes="mt-4"
              onClick={undefined}
              isLoading={bulkPatchDefaultCategories.isLoading}
              disabled={!selectedField || bulkPatchDefaultCategories.isLoading}
            />
          </div>
        </form>
      </Modal>
    </>
  );
}